import { useState, type ReactNode } from 'react'

import { AddendumsCard } from '@/components/AddendumsCard'
import { LockedStatusCard } from '@/components/LockedStatusCard'
import { RevisionsCard } from '@/components/RevisionsCard'
import { ServiceDeltasList } from '@/components/ServiceDeltasList'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import { asLockedStatus, isServiceLocked } from '@/lib/serviceLock'
import type {
  Addendum,
  AddendumChange,
  ServiceRevision,
  ServiceStatus,
  StatusChangeLog,
} from '@/lib/schemas/db'

/**
 * How a locked record can be amended: addendums stack on top of it and the
 * record itself is never rewritten.
 */
export interface ServiceAmendContract {
  /** Oldest first. */
  addendums: Addendum[]
  /** Values as the service was locked, for the "show changes since" lens. */
  lockedValues: Record<string, string | undefined>
  /** Values in force now, with every addendum applied. */
  currentValues: Record<string, string | undefined>
  amending: boolean
  /** Staged edits, already diffed against `currentValues`. */
  pendingChanges: AddendumChange[]
  onStart: () => void
  onDiscard: () => void
  onSave: (reason: string, changes: AddendumChange[]) => void | Promise<void>
}

/**
 * The older escape hatch: move the record back to an editable status. Used for
 * services that have no amend flow wired yet.
 */
export interface ServiceUnlockContract {
  actionLabel: string
  targetStatus: string
  onChangeStatus?: (status: never, reason?: string) => void | Promise<void>
  requireReason?: boolean
  actionTitle?: string
  actionDescription?: string
  lockedMessage?: string
  reasonLabel?: string
  reasonPlaceholder?: string
  confirmLabel?: string
  cancelLabel?: string
}

export interface ServiceDetailsShellProps {
  /** Used throughout the copy, e.g. "Callout", "TeamViewer Session", "Job". */
  resourceLabel: string
  /**
   * Plural noun for the read-only hint, e.g. "callouts". Separate from
   * `resourceLabel` because that can be a specific variant ("TeamViewer
   * Session") that reads wrong in a generic sentence.
   */
  resourcePlural: string
  /** Page heading, e.g. "Callout details". */
  title: string
  /** One line under the heading. */
  blurb: string
  /** Label for the identifier line, e.g. "Callout ID". */
  idLabel: string
  idValue: string
  status: ServiceStatus
  statusChanges?: StatusChangeLog[]
  getAuthorDisplayName?: (authorId: string) => string
  /** False renders the body bare, for embedding in another page. */
  showHeader?: boolean
  /** Provide to offer the amend flow. Omit to fall back to `unlock`. */
  amend?: ServiceAmendContract
  unlock?: ServiceUnlockContract
  /**
   * The add-only trail of detail edits made while the record was open,
   * appended server-side on every update. Oldest first. Distinct from
   * addendums (amendments to a locked record) and status changes.
   */
  revisions?: ServiceRevision[]
  /** The service's own section stack — order and extras are the caller's call. */
  children: ReactNode
}

/**
 * The chrome every service detail page shares: identity header, status
 * history, and the entire story of what a *locked* record offers — the
 * addendum trail, the amend affordance, the changes-since lens, or the older
 * unlock-by-status-change fallback.
 *
 * Callers supply their own section stack as children, so what differs between
 * callouts, sessions and jobs (which sections, in what order, plus job-only
 * cards) stays with them, while the parts that must not drift live here once.
 */
export function ServiceDetailsShell(props: ServiceDetailsShellProps) {
  const {
    resourceLabel,
    resourcePlural,
    title,
    blurb,
    idLabel,
    idValue,
    status,
    statusChanges = [],
    getAuthorDisplayName,
    showHeader = true,
    amend,
    unlock,
    revisions = [],
    children,
  } = props

  const [showDeltas, setShowDeltas] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const locked = isServiceLocked(status)
  const resolveAuthorName = (authorId: string) =>
    getAuthorDisplayName?.(authorId) ?? authorId

  // The transition that locked this record — who closed it, and when.
  const lockStatusChange = locked
    ? [...statusChanges].reverse().find((change) => change.newStatus === status)
    : undefined

  const handleSaveAddendum = async (
    reason: string,
    changes: AddendumChange[],
  ) => {
    if (!amend) return
    setSaveError(null)
    try {
      // `changes` is the card's snapshot from when the dialog opened, not the
      // live diff — see AddendumsCard.stagedChanges.
      await amend.onSave(reason, changes)
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Could not save the addendum.',
      )
      throw error
    }
  }

  const lockedHint = amend
    ? `${asLockedStatus(status)} ${resourcePlural} are read-only. Amend to record a change.`
    : `${asLockedStatus(status)} ${resourcePlural} are read-only. ${unlock?.actionLabel ?? 'Change the status'} to edit.`

  const body = (
    <>
      {showHeader ? (
        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {idLabel}: {idValue}
            </p>
            <h1 className="text-2xl font-semibold">{title}</h1>
            <p className="text-sm text-slate-600">{blurb}</p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <StatusHistoryPill
              currentStatus={status}
              statusChanges={statusChanges}
              getAuthorDisplayName={getAuthorDisplayName}
            />
            {locked ? (
              <span className="text-xs text-amber-700">{lockedHint}</span>
            ) : null}
          </div>
        </header>
      ) : null}

      {locked && amend ? (
        <AddendumsCard
          resourceLabel={resourceLabel}
          status={asLockedStatus(status)}
          addendums={amend.addendums}
          getAuthorDisplayName={resolveAuthorName}
          lockedAt={lockStatusChange?.createdAt}
          lockedByName={
            lockStatusChange
              ? resolveAuthorName(lockStatusChange.authorId)
              : undefined
          }
          amending={amend.amending}
          onStartAmend={amend.onStart}
          onDiscardAmend={() => {
            setSaveError(null)
            amend.onDiscard()
          }}
          pendingChanges={amend.pendingChanges}
          onSaveAddendum={handleSaveAddendum}
          showDeltas={showDeltas}
          onToggleDeltas={setShowDeltas}
          saveError={saveError}
        />
      ) : null}

      {locked && !amend && unlock ? (
        <LockedStatusCard
          resourceLabel={resourceLabel}
          status={asLockedStatus(status)}
          actionLabel={unlock.actionLabel}
          actionTitle={unlock.actionTitle}
          actionDescription={unlock.actionDescription}
          lockedMessage={unlock.lockedMessage}
          reasonLabel={unlock.reasonLabel}
          reasonPlaceholder={unlock.reasonPlaceholder}
          confirmLabel={unlock.confirmLabel}
          cancelLabel={unlock.cancelLabel}
          requireReason={unlock.requireReason}
          onAction={(reason) =>
            unlock.onChangeStatus?.(unlock.targetStatus as never, reason)
          }
        />
      ) : null}

      {amend && showDeltas && !amend.amending ? (
        <ServiceDeltasList
          lockedValues={amend.lockedValues}
          currentValues={amend.currentValues}
          statusLabel={status}
        />
      ) : null}

      <RevisionsCard
        revisions={revisions}
        getAuthorDisplayName={resolveAuthorName}
      />

      {children}
    </>
  )

  if (!showHeader) return <div className="space-y-4">{body}</div>

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto max-w-container space-y-4 px-4 py-6">{body}</main>
    </div>
  )
}
